import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { AppDispatch } from '@/store/store'
import { registerUser } from '@/store/slices/authSlice'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import PasswordValidation from '@/components/ui/PasswordValidation'
import PasswordInput from '@/components/ui/PasswordInput'
import EmailValidation from '@/components/ui/EmailValidation'
import NameValidation from '@/components/ui/NameValidation'
import PhoneInput from '@/components/ui/PhoneInput'
import Logo from '@/components/ui/Logo'
import { CountryCode } from '@/utils/countryCodes'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { useDraftSave } from '@/hooks/useDraftSave'

interface RegisterFormData {
    email: string
    password: string
    confirmPassword: string
    role: 'worker' | 'employer'
    firstName: string
    lastName: string
    phone: string
    countryInfo: CountryCode | null
    fullPhoneNumber: string
    agreedToTerms: boolean
}

const RegisterPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { showError } = useSnackbarContext()

    const [formData, setFormData] = useState<RegisterFormData>({
        email: '',
        password: '',
        confirmPassword: '',
        role: (searchParams.get('role') as 'worker' | 'employer') || 'worker',
        firstName: '',
        lastName: '',
        phone: '',
        countryInfo: null as CountryCode | null,
        fullPhoneNumber: '',
        agreedToTerms: false
    })

    const [isLoading, setIsLoading] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [agreedToTerms, setAgreedToTerms] = useState(false)

    // Memoize onRestore callback to prevent unnecessary effect re-runs
    const handleRestoreDraft = useCallback((draft: RegisterFormData) => {
        setFormData({
            email: draft.email || '',
            password: draft.password || '',
            confirmPassword: draft.confirmPassword || '',
            role: draft.role || 'worker',
            firstName: draft.firstName || '',
            lastName: draft.lastName || '',
            phone: draft.phone || '',
            countryInfo: draft.countryInfo || null,
            fullPhoneNumber: draft.fullPhoneNumber || '',
            agreedToTerms: draft.agreedToTerms || false
        })
        if (draft.agreedToTerms) {
            setAgreedToTerms(true)
        }
    }, [])

    // Initialize draft save utility
    const { draftSave, clearDraft } = useDraftSave({
        pageId: 'register',
        debounceMs: 1000,
        autoRestore: true,
        onRestore: handleRestoreDraft
    })

    useEffect(() => {
        const roleParam = searchParams.get('role')
        if (roleParam && ['worker', 'employer'].includes(roleParam)) {
            setFormData(prev => ({ ...prev, role: roleParam as 'worker' | 'employer' }))
        }
    }, [searchParams])

    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        // Email validation
        const email = formData.email.trim()
        if (!email) {
            newErrors.email = 'Email is required'
        } else {
            const emailErrors = []
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                emailErrors.push('valid email format')
            }
            if (!email.includes('@')) {
                emailErrors.push('@ symbol')
            }
            if (!/\.[a-zA-Z]{2,}$/.test(email)) {
                emailErrors.push('domain extension')
            }
            if (/\s/.test(email)) {
                emailErrors.push('no spaces allowed')
            }
            if (email.length < 5) {
                emailErrors.push('at least 5 characters')
            }

            if (emailErrors.length > 0) {
                newErrors.email = `Email must have: ${emailErrors.join(', ')}`
            }
        }

        // First name validation
        const firstName = formData.firstName.trim()
        if (!firstName) {
            newErrors.firstName = 'First name is required'
        } else {
            const firstNameErrors = []
            if (firstName.length < 3) {
                firstNameErrors.push('at least 3 characters')
            }
            if (/\d/.test(firstName)) {
                firstNameErrors.push('no numbers')
            }
            if (!/^[a-zA-Z\s]*$/.test(firstName)) {
                firstNameErrors.push('only letters and spaces')
            }
            if (firstName !== firstName.trim()) {
                firstNameErrors.push('no leading/trailing spaces')
            }
            if (firstName.length > 50) {
                firstNameErrors.push('maximum 50 characters')
            }

            if (firstNameErrors.length > 0) {
                newErrors.firstName = `First name must have: ${firstNameErrors.join(', ')}`
            }
        }

        // Last name validation
        const lastName = formData.lastName.trim()
        if (!lastName) {
            newErrors.lastName = 'Last name is required'
        } else {
            const lastNameErrors = []
            if (lastName.length < 3) {
                lastNameErrors.push('at least 3 characters')
            }
            if (/\d/.test(lastName)) {
                lastNameErrors.push('no numbers')
            }
            if (!/^[a-zA-Z\s]*$/.test(lastName)) {
                lastNameErrors.push('only letters and spaces')
            }
            if (lastName !== lastName.trim()) {
                lastNameErrors.push('no leading/trailing spaces')
            }
            if (lastName.length > 50) {
                lastNameErrors.push('maximum 50 characters')
            }

            if (lastNameErrors.length > 0) {
                newErrors.lastName = `Last name must have: ${lastNameErrors.join(', ')}`
            }
        }

        // Password validation matching our component requirements
        const password = formData.password
        const passwordErrors = []

        if (password.length < 10) {
            passwordErrors.push('minimum 10 characters')
        }
        if (!/\d/.test(password)) {
            passwordErrors.push('1 number')
        }
        if (!/[a-z]/.test(password)) {
            passwordErrors.push('1 lowercase letter')
        }
        if (!/[A-Z]/.test(password)) {
            passwordErrors.push('1 uppercase letter')
        }
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            passwordErrors.push('1 special character')
        }
        if (/(.)\1{2,}/.test(password)) {
            passwordErrors.push('no more than 2 consecutive identical characters')
        }

        if (passwordErrors.length > 0) {
            newErrors.password = `Password must have: ${passwordErrors.join(', ')}`
        }

        // Confirm password validation
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!agreedToTerms) {
            showError('Please accept the Terms of Service and Privacy Policy to continue.', {
                position: 'bottom-center',
                duration: 6000
            })
            return
        }

        if (!validateForm()) return

        setIsLoading(true)

        try {
            const { confirmPassword, agreedToTerms: _, ...userData } = formData
            const result = await dispatch(registerUser(userData)).unwrap()

            clearDraft()

            navigate('/dashboard', {
                replace: true
            })

        } catch (error) {
            // Error is handled by the slice and shown via toast
            console.error('Registration error:', error)
        } finally {
            setIsLoading(false)
        }
    }



    // Helper function to clear errors when user starts typing in validation components
    const clearError = (fieldName: string) => {
        if (errors[fieldName]) {
            setErrors(prev => ({ ...prev, [fieldName]: '' }))
        }
    }

    return (
        <>
            <Helmet>
                <title>Sign Up - SideGO</title>
                <meta name="description" content="Create your SideGO account and start your journey in the global gig economy." />
            </Helmet>

            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <div className="flex justify-center">
                            <Logo
                                size="lg"
                                showText={true}
                                className="justify-center"
                                variant="default"
                            />
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                            Create your account
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                            Already have an account?{' '}
                            <Link
                                to="/login"
                                className="font-medium text-primary-600 hover:text-primary-500"
                            >
                                Sign in
                            </Link>
                        </p>
                    </div>

                    {/* Role Selection */}
                    <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 p-1">
                        {[
                            { value: 'worker', label: 'I want to work' },
                            { value: 'employer', label: 'I want to hire' }
                        ].map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                    const newData = { ...formData, role: option.value as 'worker' | 'employer' }
                                    setFormData(newData)
                                    draftSave(newData)
                                }}
                                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${formData.role === option.value
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
                        {/* Name Fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <NameValidation
                                    value={formData.firstName}
                                    onChange={(value) => {
                                        const newData = { ...formData, firstName: value }
                                        setFormData(newData)
                                        draftSave(newData)
                                        clearError('firstName')
                                    }}
                                    placeholder="Enter your first name"
                                    label="First name"
                                    fieldName="firstName"
                                    showValidation={true}
                                    required
                                    autoComplete="given-name"
                                    error={errors.firstName}
                                />
                            </div>

                            <div>
                                <NameValidation
                                    value={formData.lastName}
                                    onChange={(value) => {
                                        const newData = { ...formData, lastName: value }
                                        setFormData(newData)
                                        draftSave(newData)
                                        clearError('lastName')
                                    }}
                                    placeholder="Enter your last name"
                                    label="Last name"
                                    fieldName="lastName"
                                    showValidation={true}
                                    required
                                    autoComplete="family-name"
                                    error={errors.lastName}
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Email address
                            </label>
                            <EmailValidation
                                value={formData.email}
                                onChange={(value) => {
                                    const newData = { ...formData, email: value }
                                    setFormData(newData)
                                    draftSave(newData)
                                    clearError('email')
                                }}
                                placeholder="Enter your email address"
                                showValidation={true}
                                required
                                autoComplete="email"
                                error={errors.email}
                            />
                        </div>

                        {/* Phone Number */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Phone Number (optional)
                            </label>
                            <PhoneInput
                                value={formData.phone}
                                onChange={(phoneNumber, countryInfo, fullNumber) => {
                                    const newData = {
                                        ...formData,
                                        phone: phoneNumber,
                                        countryInfo: countryInfo,
                                        fullPhoneNumber: fullNumber
                                    }
                                    setFormData(newData)
                                    draftSave(newData)
                                    clearError('phone')
                                }}
                                placeholder="Enter your phone number"
                                error={errors.phone}
                                autoComplete="tel"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Password
                            </label>
                            <PasswordValidation
                                value={formData.password}
                                onChange={(value) => {
                                    const newData = { ...formData, password: value }
                                    setFormData(newData)
                                    draftSave(newData)
                                    clearError('password')
                                }}
                                placeholder="Create a strong password"
                                showValidation={true}
                                name="password"
                                id="password"
                                required
                            />
                            {errors.password && <p className="mt-2 text-xs text-red-600">{errors.password}</p>}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Confirm password
                            </label>
                            <PasswordInput
                                value={formData.confirmPassword}
                                onChange={(value) => {
                                    const newData = { ...formData, confirmPassword: value }
                                    setFormData(newData)
                                    draftSave(newData)
                                    clearError('confirmPassword')
                                }}
                                placeholder="Confirm your password"
                                name="confirmPassword"
                                id="confirmPassword"
                                required
                                autoComplete="new-password"
                                error={errors.confirmPassword}
                            />
                            {errors.confirmPassword && <p className="mt-2 text-xs text-red-600">{errors.confirmPassword}</p>}
                        </div>

                        {/* Terms */}
                        <div className="flex items-center">
                            <input
                                id="agree-terms"
                                name="agree-terms"
                                type="checkbox"
                                checked={agreedToTerms}
                                onChange={(e) => {
                                    const newData = { ...formData, agreedToTerms: e.target.checked }
                                    setAgreedToTerms(e.target.checked)
                                    setFormData(newData)
                                    draftSave(newData)
                                }}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:checked:bg-primary-600"
                            />
                            <label htmlFor="agree-terms" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                                I agree to the{' '}
                                <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                                    Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                                    Privacy Policy
                                </Link>
                            </label>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {isLoading ? (
                                    <LoadingSpinner size="sm" color="white" />
                                ) : (
                                    'Create account'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}

export default RegisterPage